/**
 * Profile insights, derived from ascents and the peak index only (nothing is stored). Pure, so the
 * profile screen and the story image share the same numbers and unit tests pin them down. First
 * ascents and the weekly streak come from src/shared/stats.ts, the same rules the server applies to
 * what friends see.
 */
import type { Activity, BucketList } from "../../shared/schemas";
import { haversine } from "../../shared/geo";
import { activeWeeks, computeStreak, EVEREST_M, firstAscents, weekIndexOf, type Streak } from "../../shared/stats";
import type { PeakLite } from "./catalog";
import type { ClientAscent } from "./ops";

export { EVEREST_M };
const TRESMILES_LIST = "tresmiles";

type Get = (id: string) => PeakLite | undefined;

export type FirstAscent = { date: string; activity: Activity };

export type YearSummary = {
  year: number;
  ascents: number;
  /** Peaks whose first ever ascent fell in this year. */
  newPeaks: number;
  /** Distinct peaks climbed this year. */
  peaks: number;
  peakIds: string[];
  gain: number;
  hasGain: boolean;
  /** Ascents per month, January first. */
  months: number[];
  highest: PeakLite | null;
};

export type Zone = { massif: string; climbed: number; total: number };

export type Fact =
  | { kind: "everest"; times: number; gain: number }
  | { kind: "streak"; months: number; from: string; to: string }
  | { kind: "weeks"; streak: Streak }
  | { kind: "favorite"; peak: PeakLite; times: number }
  | { kind: "bigDay"; peaks: number; date: string; ascentId: string }
  | { kind: "span"; km: number; west: PeakLite; east: PeakLite }
  | { kind: "winter"; ascents: number }
  | { kind: "month"; month: number; ascents: number }
  | { kind: "tresmiles"; done: number; total: number }
  | { kind: "first"; peak: PeakLite; date: string };

export type Insights = {
  total: {
    ascents: number;
    peaks: number;
    gain: number;
    hasGain: boolean;
    distanceKm: number;
    hasDistance: boolean;
    threeThousanders: number;
    highest: PeakLite | null;
    since: string | null;
  };
  /** First ascent (date and activity) per peak: the stamp each peak earned. */
  firsts: Map<string, FirstAscent>;
  thisYear: YearSummary;
  lastYear: YearSummary;
  /** Last year's ascents up to today's day of the year: a fair comparison mid-season. */
  lastYearToDate: number;
  zones: Zone[];
  facts: Fact[];
  /** Weeks in a row with an ascent ("Racha de semanas"), with refugio weeks and pauses. */
  streak: Streak;
};

function emptyYear(year: number): YearSummary {
  return { year, ascents: 0, newPeaks: 0, peaks: 0, peakIds: [], gain: 0, hasGain: false, months: Array.from({ length: 12 }, () => 0), highest: null };
}

const higher = (a: PeakLite | null, b: PeakLite | undefined) => (b?.elevation != null && (!a || b.elevation > (a.elevation ?? 0)) ? b : a);

/** Month index since year 0, so consecutive calendar months differ by one. */
const monthIndex = (date: string) => Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7)) - 1;
const monthIso = (i: number) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;

/** The local calendar date of `now`, as the climber's own dates are written. */
export const localDate = (now: Date) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

export function computeInsights(
  ascents: readonly ClientAscent[],
  get: Get,
  allPeaks: readonly PeakLite[],
  lists: readonly BucketList[],
  now = new Date(),
  pauses: readonly string[] = [],
): Insights {
  const sorted = [...ascents].sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt));
  const firstOf = firstAscents(sorted);
  const year = now.getFullYear();
  const years = new Map<number, { summary: YearSummary; peaks: Set<string> }>([
    [year, { summary: emptyYear(year), peaks: new Set() }],
    [year - 1, { summary: emptyYear(year - 1), peaks: new Set() }],
  ]);

  const firsts = new Map<string, FirstAscent>([...firstOf].map(([id, a]) => [id, { date: a.date, activity: a.activity }]));
  const times = new Map<string, number>();
  const months = new Set<number>();
  const perMonth = Array.from({ length: 12 }, () => 0);
  let gain = 0;
  let hasGain = false;
  let distanceKm = 0;
  let hasDistance = false;
  let winter = 0;
  let bigDay: Extract<Fact, { kind: "bigDay" }> | null = null;
  let lastYearToDate = 0;
  const today = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  for (const a of sorted) {
    if (a.elevationGain != null) {
      gain += a.elevationGain;
      hasGain = true;
    }
    if (a.distanceKm != null) {
      distanceKm += a.distanceKm;
      hasDistance = true;
    }
    if (a.winter) winter++;
    months.add(monthIndex(a.date));
    const month = Number(a.date.slice(5, 7)) - 1;
    perMonth[month]!++;
    if (a.peakIds.length > 1 && (!bigDay || a.peakIds.length > bigDay.peaks)) bigDay = { kind: "bigDay", peaks: a.peakIds.length, date: a.date, ascentId: a.id };

    if (a.year === year - 1 && a.date.slice(5) <= today) lastYearToDate++;
    const y = years.get(a.year);
    if (y) {
      y.summary.ascents++;
      y.summary.months[month]!++;
      if (a.elevationGain != null) {
        y.summary.gain += a.elevationGain;
        y.summary.hasGain = true;
      }
    }
    for (const id of a.peakIds) {
      times.set(id, (times.get(id) ?? 0) + 1);
      if (y && firstOf.get(id) === a) y.summary.newPeaks++;
      if (y) {
        y.peaks.add(id);
        y.summary.highest = higher(y.summary.highest, get(id));
      }
    }
  }
  for (const y of years.values()) {
    y.summary.peaks = y.peaks.size;
    y.summary.peakIds = [...y.peaks];
  }

  // Peak-level totals and zones.
  let highest: PeakLite | null = null;
  let three = 0;
  let west: PeakLite | null = null;
  let east: PeakLite | null = null;
  const climbedByMassif = new Map<string, number>();
  for (const id of firsts.keys()) {
    const p = get(id);
    if (!p) continue;
    highest = higher(highest, p);
    if ((p.elevation ?? 0) >= 3000) three++;
    // The span is along the range the catalog covers: a Mont Blanc logged by hand is not "cordillera".
    if (p.inCatalog && (!west || p.lon < west.lon)) west = p;
    if (p.inCatalog && (!east || p.lon > east.lon)) east = p;
    if (p.massif) climbedByMassif.set(p.massif, (climbedByMassif.get(p.massif) ?? 0) + 1);
  }
  const totalByMassif = new Map<string, number>();
  for (const p of allPeaks) if (p.massif && climbedByMassif.has(p.massif)) totalByMassif.set(p.massif, (totalByMassif.get(p.massif) ?? 0) + 1);
  const zones: Zone[] = [...climbedByMassif]
    .map(([massif, climbed]) => ({ massif, climbed, total: Math.max(climbed, totalByMassif.get(massif) ?? 0) }))
    .sort((a, b) => b.climbed - a.climbed || b.climbed / b.total - a.climbed / a.total || a.massif.localeCompare(b.massif, "es"));

  // Facts: only the ones with something worth saying, most striking first.
  const facts: Fact[] = [];
  if (hasGain && gain >= EVEREST_M / 2) facts.push({ kind: "everest", times: gain / EVEREST_M, gain });

  const { reached: _, ...weeks } = computeStreak(activeWeeks(sorted).keys(), pauses.map(weekIndexOf), localDate(now));
  if (weeks.current >= 1 || weeks.best >= 2) facts.push({ kind: "weeks", streak: weeks });

  const streak = longestRun([...months].sort((a, b) => a - b));
  if (streak.length >= 2) facts.push({ kind: "streak", months: streak.length, from: monthIso(streak.from), to: monthIso(streak.from + streak.length - 1) });

  if (west && east && west !== east) {
    // Along the parallel: the span of the range covered, not the diagonal between the two.
    const km = haversine(west.lat, west.lon, west.lat, east.lon) / 1000;
    if (km >= 10) facts.push({ kind: "span", km, west, east });
  }

  let favorite: Extract<Fact, { kind: "favorite" }> | null = null;
  for (const [id, n] of times) {
    const p = get(id);
    if (p && n >= 2 && (!favorite || n > favorite.times || (n === favorite.times && (p.elevation ?? 0) > (favorite.peak.elevation ?? 0)))) favorite = { kind: "favorite", peak: p, times: n };
  }
  if (favorite) facts.push(favorite);
  if (bigDay) facts.push(bigDay);

  const tresmiles = lists.find((l) => l.id === TRESMILES_LIST);
  if (tresmiles?.items.length) {
    const done = tresmiles.items.filter((i) => firsts.has(i.peakId)).length;
    if (done) facts.push({ kind: "tresmiles", done, total: tresmiles.items.length });
  }

  if (winter) facts.push({ kind: "winter", ascents: winter });

  if (sorted.length >= 3) {
    const best = perMonth.reduce((m, n, i) => (n > perMonth[m]! ? i : m), 0);
    facts.push({ kind: "month", month: best, ascents: perMonth[best]! });
  }

  const first = sorted[0];
  const firstPeak = first ? first.peakIds.map(get).reduce<PeakLite | null>(higher, null) : null;
  if (first && firstPeak) facts.push({ kind: "first", peak: firstPeak, date: first.date });

  return {
    total: {
      ascents: ascents.length,
      peaks: firsts.size,
      gain,
      hasGain,
      distanceKm,
      hasDistance,
      threeThousanders: three,
      highest,
      since: sorted[0]?.date ?? null,
    },
    firsts,
    thisYear: years.get(year)!.summary,
    lastYear: years.get(year - 1)!.summary,
    lastYearToDate,
    zones,
    facts,
    streak: weeks,
  };
}

/** Longest run of consecutive integers in a sorted, de-duplicated array. */
function longestRun(sorted: number[]): { from: number; length: number } {
  let best = { from: 0, length: 0 };
  let from = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0 || sorted[i] !== sorted[i - 1]! + 1) from = i;
    const length = i - from + 1;
    if (length > best.length) best = { from: sorted[from]!, length };
  }
  return best;
}

/**
 * The stamps to show off: the pinned ones that are still climbed, in order. With `fill`, the
 * highest other climbed peaks fill the free places (the story image always has a full row).
 */
export function showcase(pins: readonly string[], firsts: ReadonlyMap<string, FirstAscent>, get: Get, max: number, fill = false): string[] {
  const out = pins.filter((id) => firsts.has(id)).slice(0, max);
  if (!fill || out.length >= max) return out;
  const rest = [...firsts.keys()]
    .filter((id) => !out.includes(id))
    .sort((a, b) => (get(b)?.elevation ?? 0) - (get(a)?.elevation ?? 0) || firsts.get(a)!.date.localeCompare(firsts.get(b)!.date));
  return [...out, ...rest.slice(0, max - out.length)];
}
